import React, {useMemo, useState} from 'react';

import {TFunction} from 'i18next';
import {useTranslation} from 'react-i18next';
import {generatePath} from 'react-router-dom';
import {toast} from 'react-toastify';

import {ReactComponent as CopyIcon} from 'assets/svg/copy.svg';
import {HeaderLeft} from 'components/pages/DocumentsCreate/ConsumerReturn/ReturnInfo/styled';
import {Icon} from 'components/ui-kit/Icon';
import {Link} from 'components/ui-kit/Link';
import {
    CopyButton,
    Id,
    StatusWrapper,
    Title,
    TitleText,
    TitleWrapper,
} from 'components/ui-kit/PageComponents';
import {Stepper} from 'components/ui-kit/Stepper';
import {DOCUMENT_STATUS_COLORS} from 'core/constants/statuses';
import {ProductsPackageType} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {colors} from 'theme';
import {
    DisaggregationType,
    ProductContentAccessors,
    ProductContentFormattedRow,
    ProductContentRawRow,
} from 'types';
import {DisaggregationReport} from 'types/documents';
import {useClipboard} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {toHex} from 'utils/string';

import {ProductsTable} from './Products/Table';
import {
    Error,
    ErrorsWrapper,
    GeneralInfoContent,
    InfoList,
    InfoListLabel,
    InfoListRow,
    InfoListValue,
    InfoListValueRow,
} from './styled';

interface DisaggregationProps {
    data: DisaggregationReport;
}

const translateEnum = (t: TFunction, key: string, value: string) => t(key, {defaultValue: value});

const getProductsConfig = (t: TFunction, withMethod: boolean) => [
    {
        Header: t('pages.documents.details.Disaggregation.packagingType'),
        accessor: ProductContentAccessors.packageType,
        disableSortBy: true,
        width: withMethod ? 335 : 95,
    },
    {
        Header: t('pages.documents.details.Disaggregation.markingCode'),
        accessor: ProductContentAccessors.code,
        disableSortBy: true,
        ...(withMethod ? {width: 460} : {}),
    },
    ...(withMethod
        ? [
              {
                  Header: t('pages.documents.details.Disaggregation.disaggregationMethod'),
                  accessor: ProductContentAccessors.disaggregationMethod,
                  disableSortBy: true,
              },
          ]
        : []),
];

const convertProducts = (
    data: ProductContentRawRow[] | undefined,
    t: TFunction,
): ProductContentFormattedRow[] => {
    if (!data?.length) return [];

    return data.map((elem) => ({
        [ProductContentAccessors.packageType]: handleNil(
            elem.packagingType,
            (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
        ),
        [ProductContentAccessors.code]: handleNil(elem.markingCode, (value: string) => (
            <Link
                to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                    id: toHex(`${elem.gtin}§${value}`),
                })}
                style={{wordBreak: 'break-all'}}
            >
                {value}
            </Link>
        )),
        [ProductContentAccessors.name]: handleNil(elem.productName),
        [ProductContentAccessors.status]: '',
        [ProductContentAccessors.modifiedDate]: '',
        [ProductContentAccessors.manufacturer]: '',
        [ProductContentAccessors.disaggregationMethod]: handleNil(
            elem.disaggregationMethod,
            (value: string) =>
                t(`pages.documents.details.Disaggregation.methods.${value}`, {defaultValue: value}),
        ),
    }));
};

const GeneralInfo = ({data}: DisaggregationProps) => {
    const {t} = useTranslation();
    const {reportInfo} = data;
    const fields = [
        {
            key: 'documentDate',
            value: handleNil(data.createdAt, (value: string) => formatDate(value, 'yyyy-MM-dd')),
        },
        {
            key: 'disaggregationType',
            value: handleNil(reportInfo?.disaggregationType, (value: DisaggregationType) =>
                t(`pages.documents.details.Disaggregation.types.${value}`),
            ),
        },
        {key: 'operator', value: handleNil(reportInfo?.operator)},
        {key: 'organization', value: handleNil(reportInfo?.organization)},
        {
            key: 'sourceFormation',
            value: handleNil(reportInfo?.source, (value: string) =>
                translateEnum(t, `dictionaries.documents.DropOut.sourceFormation.${value}`, value),
            ),
        },
    ];

    return (
        <GeneralInfoContent>
            {!!data.errors?.length && (
                <ErrorsWrapper>
                    {data.errors.map((error, index) => (
                        <Error key={index}>{error.message ?? ''}</Error>
                    ))}
                </ErrorsWrapper>
            )}
            <InfoList>
                {fields.map(({key, value}) => (
                    <InfoListRow key={key}>
                        <InfoListLabel>
                            {t(`pages.documents.details.Disaggregation.${key}`)}
                        </InfoListLabel>
                        <InfoListValueRow>
                            <InfoListValue>{value}</InfoListValue>
                        </InfoListValueRow>
                    </InfoListRow>
                ))}
            </InfoList>
        </GeneralInfoContent>
    );
};

const Products = ({data}: DisaggregationProps) => (
    <ProductsTable
        reportId={data.reportId}
        configFunc={(t) =>
            getProductsConfig(t, data.reportInfo?.disaggregationType === 'TRANSPORT_PACKAGING')
        }
        dataMapper={convertProducts}
    />
);

export const Disaggregation = ({data}: DisaggregationProps) => {
    const {t} = useTranslation();
    const copy = useClipboard();
    const [step, setStep] = useState(0);
    const status = useMemo(
        () =>
            handleNil(data.status, (value: string) => t(`dictionaries.documents.status.${value}`)),
        [data.status, t],
    );

    const copyCode = () => {
        copy(data.reportId);
        toast.success(t('pages.documents.details.idCopied'));
    };

    return (
        <>
            <TitleWrapper>
                <HeaderLeft>
                    <TitleText>
                        <Title>
                            {t('pages.documents.details.Disaggregation.DisaggregationId')}{' '}
                            <Id>{handleNil(data.reportId)}</Id>
                        </Title>
                        <CopyButton
                            icon={
                                <Icon
                                    source={CopyIcon}
                                    width={15}
                                    height={15}
                                    fillColor={colors.brandPrimary}
                                />
                            }
                            onClick={copyCode}
                            hoverIndication
                            label={t('pages.documents.details.copyId')}
                            labelPlace="bottom"
                        />
                    </TitleText>
                    <StatusWrapper color={DOCUMENT_STATUS_COLORS[data.status ?? 'DEFAULT']}>
                        {status}
                    </StatusWrapper>
                </HeaderLeft>
            </TitleWrapper>
            <Stepper
                steps={[
                    {
                        label: t('pages.documents.detail.commonInfo'),
                        component: <GeneralInfo data={data} />,
                    },
                    {
                        label: t('pages.documents.detail.products'),
                        component: <Products data={data} />,
                    },
                ]}
                activeStep={step}
                isClickable
                clickCallback={setStep}
            />
        </>
    );
};
